import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Database } from '@cafe-pos/supabase';
import { BOOKING_SOURCES, BOOKING_PAYMENT_OPTIONS, BOOKING_PAYMENT_STATUSES, composeBookingPhone, parseStoredBookingPhone, formatBookingAmount, parseBookingAmount, parseBookingFiscal, bookingRequiresFiscalRefund, DEFAULT_PHONE_COUNTRY_ISO, isValidGuestEmail, type BookingSource, type BookingPaymentOption, type BookingPaymentStatus, type PhoneCountryIso } from '@cafe-pos/shared';
import { supabase } from '../supabase.js';
import { PhoneCountryInput } from '../PhoneCountryInput.js';
import { Button, Callout, Field, Modal, PageHeader, SegmentedControl } from '../ui/index.js';

type RoomRow = Database['public']['Tables']['rooms']['Row'];
type BookingRow = Database['public']['Tables']['bookings']['Row'];

export const SOURCE_COLORS: Record<BookingSource, string> = {
  airbnb: 'var(--chart-5)',
  booking: 'var(--chart-1)',
  expedia: 'var(--chart-3)',
  web: 'var(--chart-2)',
  instagram: 'var(--chart-5)',
  on_site: 'var(--chart-6)',
  other: 'var(--chart-4)',
};

function statusBadgeClass(status: string): string {
  if (status === 'pending') return 'badge badge-warning';
  if (status === 'rejected') return 'badge badge-danger';
  if (status === 'cancelled') return 'badge badge-muted';
  return 'badge badge-success';
}

function statusLabelKey(status: string): string {
  if (status === 'pending') return 'bookings.statusPending';
  if (status === 'rejected') return 'bookings.statusRejected';
  if (status === 'cancelled') return 'bookings.cancelled';
  return 'bookings.statusConfirmed';
}

function payBadgeClass(status: string): string {
  if (status === 'paid') return 'badge badge-success';
  if (status === 'pending') return 'badge badge-warning';
  if (status === 'failed') return 'badge badge-danger';
  return 'badge badge-muted';
}

interface BookingForm {
  room_id: string;
  guest_name: string;
  phone: string;
  phoneIso: PhoneCountryIso;
  guest_email: string;
  check_in: string;
  check_out: string;
  adults: number;
  kids: number;
  source: BookingSource;
  payment_option: BookingPaymentOption;
  payment_status: BookingPaymentStatus;
  amount_amd: number;
  note: string;
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return isoDate(d);
}

function nightsBetween(checkIn: string, checkOut: string): number {
  return Math.max(
    1,
    Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86_400_000),
  );
}

function amountForStay(room: RoomRow | undefined, checkIn: string, checkOut: string): number {
  const nightly = Number(room?.price_amd) || 0;
  return nightly * nightsBetween(checkIn, checkOut);
}

const OVERLAP_PG_CODE = '23P01';

type ViewMode = 'calendar' | 'table';

export function BookingsView({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'hy' ? 'hy-AM' : 'en-US';
  const [rooms, setRooms] = useState<RoomRow[]>([]);
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const today = isoDate(new Date());
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  // 'create' opens a blank form; a booking id opens that booking for editing.
  const [modal, setModal] = useState<'create' | string | null>(null);
  const [form, setForm] = useState<BookingForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [reviewing, setReviewing] = useState<'confirm' | 'reject' | null>(null);
  const [reviewConfirm, setReviewConfirm] = useState<'confirm' | 'reject' | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  const monthStart = isoDate(new Date(month.year, month.month, 1));
  const daysInMonth = new Date(month.year, month.month + 1, 0).getDate();
  const monthEndExclusive = isoDate(new Date(month.year, month.month + 1, 1));

  async function load() {
    if (!supabase || !clientId) return;
    setLoading(true);
    setError(null);
    const [roomsRes, bookingsRes] = await Promise.all([
      supabase
        .from('rooms')
        .select('*')
        .eq('active', true)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true }),
      supabase
        .from('bookings')
        .select('*')
        .lt('check_in', monthEndExclusive)
        .gt('check_out', monthStart)
        .order('check_in', { ascending: true })
        .order('guest_name', { ascending: true }),
    ]);
    if (roomsRes.error) setError(roomsRes.error.message);
    else setRooms((roomsRes.data ?? []) as RoomRow[]);
    if (bookingsRes.error) setError(bookingsRes.error.message);
    else setBookings((bookingsRes.data ?? []) as BookingRow[]);
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, month.year, month.month]);

  /** roomId → (day-of-month → bookings covering that night, confirmed first). */
  const grid = useMemo(() => {
    const map = new Map<string, Map<number, BookingRow[]>>();
    for (const b of bookings) {
      let row = map.get(b.room_id);
      if (!row) {
        row = new Map();
        map.set(b.room_id, row);
      }
      for (let day = 1; day <= daysInMonth; day++) {
        const date = isoDate(new Date(month.year, month.month, day));
        if (date >= b.check_in && date < b.check_out) {
          const list = row.get(day) ?? [];
          list.push(b);
          row.set(day, list);
        }
      }
    }
    for (const row of map.values()) {
      for (const [day, list] of row) {
        list.sort((a, b) => {
          if (a.status !== b.status) return a.status === 'confirmed' ? -1 : 1;
          return a.check_in.localeCompare(b.check_in) || a.id.localeCompare(b.id);
        });
        row.set(day, list);
      }
    }
    return map;
  }, [bookings, daysInMonth, month.year, month.month]);

  const monthLabel = new Date(month.year, month.month, 1).toLocaleDateString(
    i18n.language === 'hy' ? 'hy-AM' : 'en-US',
    { month: 'long', year: 'numeric' },
  );

  const roomName = useMemo(() => {
    const map = new Map(rooms.map((r) => [r.id, r.name]));
    return (id: string) => map.get(id) ?? '—';
  }, [rooms]);

  const tableRows = useMemo(
    () =>
      [...bookings].sort(
        (a, b) =>
          a.check_in.localeCompare(b.check_in) ||
          a.guest_name.localeCompare(b.guest_name) ||
          a.id.localeCompare(b.id),
      ),
    [bookings],
  );

  const fmtDate = (iso: string) =>
    new Date(iso + 'T00:00:00').toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  function shiftMonth(delta: number) {
    setMonth(({ year, month: m }) => {
      const d = new Date(year, m + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  }

  function openCreate(roomId?: string, checkIn?: string) {
    const ci = checkIn ?? today;
    const co = addDays(ci, 1);
    const rid = roomId ?? rooms[0]?.id ?? '';
    const room = rooms.find((r) => r.id === rid);
    setForm({
      room_id: rid,
      guest_name: '',
      phone: '',
      phoneIso: DEFAULT_PHONE_COUNTRY_ISO,
      guest_email: '',
      check_in: ci,
      check_out: co,
      adults: 1,
      kids: 0,
      source: 'other',
      payment_option: 'on_site',
      payment_status: 'unpaid',
      amount_amd: amountForStay(room, ci, co),
      note: '',
    });
    setModal('create');
    setModalError(null);
    setCancelConfirm(false);
    setReviewConfirm(null);
  }

  function openEdit(b: BookingRow) {
    const parsed = parseStoredBookingPhone(b.phone);
    setForm({
      room_id: b.room_id,
      guest_name: b.guest_name,
      phone: parsed.national,
      phoneIso: parsed.iso,
      guest_email: b.guest_email ?? '',
      check_in: b.check_in,
      check_out: b.check_out,
      adults: b.adults,
      kids: b.kids,
      source: b.source as BookingSource,
      payment_option: (b.payment_option as BookingPaymentOption) || 'on_site',
      payment_status: (b.payment_status as BookingPaymentStatus) || 'unpaid',
      amount_amd: Number(b.amount_amd) || 0,
      note: b.note ?? '',
    });
    setModal(b.id);
    setModalError(null);
    setCancelConfirm(false);
    setReviewConfirm(null);
  }

  function closeModal() {
    setModal(null);
    setForm(null);
    setModalError(null);
    setReviewing(null);
    setReviewConfirm(null);
  }

  const editingBooking = modal && modal !== 'create' ? bookings.find((b) => b.id === modal) : null;

  function validate(f: BookingForm): string | null {
    if (!f.room_id) return t('bookings.roomRequired');
    if (!f.guest_name.trim()) return t('bookings.nameRequired');
    if (!f.phone.trim()) return t('bookings.phoneRequired');
    if (f.guest_email.trim() && !isValidGuestEmail(f.guest_email))
      return t('bookings.emailInvalid');
    if (!f.check_in || !f.check_out || f.check_out <= f.check_in)
      return t('bookings.datesInvalid');
    if (f.adults < 1) return t('bookings.adultsRequired');
    if (!f.payment_option) return t('bookings.paymentOptionRequired');
    if (
      f.payment_option === 'free' &&
      editingBooking &&
      editingBooking.payment_option !== 'free' &&
      (editingBooking.payment_method === 'cash' ||
        editingBooking.payment_method === 'card' ||
        !!parseBookingFiscal(editingBooking.fiscal_receipt)?.fiscal)
    ) {
      return t('bookings.freeAfterPayment');
    }
    if (f.payment_option !== 'free' && !(f.amount_amd > 0)) return t('bookings.amountRequired');
    const room = rooms.find((r) => r.id === f.room_id);
    if (room && f.adults + f.kids > room.capacity)
      return t('bookings.overCapacity', { capacity: room.capacity });
    return null;
  }

  async function handleSave() {
    if (!supabase || !clientId || !form) return;
    const invalid = validate(form);
    if (invalid) {
      setModalError(invalid);
      return;
    }
    setSaving(true);
    setModalError(null);
    const phone = composeBookingPhone(form.phoneIso, form.phone);
    const guest_email = form.guest_email.trim()
      ? form.guest_email.trim().toLowerCase()
      : null;
    const wasPaid = editingBooking?.payment_status === 'paid';
    const leavingFree =
      editingBooking?.payment_option === 'free' && form.payment_option !== 'free';
    const isFree = form.payment_option === 'free';
    const payment_status: BookingPaymentStatus = isFree
      ? 'paid'
      : form.payment_option === 'online'
        ? form.payment_status
        : leavingFree || modal === 'create'
          ? 'unpaid'
          : ((editingBooking?.payment_status as BookingPaymentStatus) || 'unpaid');
    const amount_amd = isFree ? 0 : form.amount_amd;
    const payload: Database['public']['Tables']['bookings']['Update'] = {
      room_id: form.room_id,
      guest_name: form.guest_name.trim(),
      phone,
      guest_email,
      check_in: form.check_in,
      check_out: form.check_out,
      adults: form.adults,
      kids: form.kids,
      source: form.source,
      payment_option: form.payment_option,
      amount_amd,
      note: form.note.trim() || null,
      updated_at: new Date().toISOString(),
    };
    if (isFree || leavingFree || form.payment_option === 'online' || modal === 'create') {
      payload.payment_status = payment_status;
    }
    if (isFree) {
      payload.payment_method = null;
      if (editingBooking?.payment_option !== 'free') payload.paid_at = new Date().toISOString();
    } else if (leavingFree) {
      payload.paid_at = payment_status === 'paid' ? new Date().toISOString() : null;
      payload.payment_method = null;
    } else if (form.payment_option === 'online') {
      if (payment_status === 'paid' && !wasPaid) {
        payload.paid_at = new Date().toISOString();
      }
      if (payment_status !== 'paid') {
        payload.paid_at = null;
        payload.payment_method = null;
      }
    }
    const { error: err } =
      modal === 'create'
        ? await supabase.from('bookings').insert({
            client_id: clientId,
            room_id: form.room_id,
            guest_name: form.guest_name.trim(),
            phone,
            guest_email,
            check_in: form.check_in,
            check_out: form.check_out,
            adults: form.adults,
            kids: form.kids,
            source: form.source,
            payment_option: form.payment_option,
            payment_status,
            amount_amd,
            note: form.note.trim() || null,
            origin: 'dashboard',
            status: 'confirmed',
            ...(payment_status === 'paid' ? { paid_at: new Date().toISOString() } : {}),
          })
        : await supabase.from('bookings').update(payload).eq('id', modal!);
    setSaving(false);
    if (err) {
      setModalError(err.code === OVERLAP_PG_CODE ? t('bookings.conflict') : err.message);
      return;
    }
    closeModal();
    void load();
  }

  async function handleCancelBooking() {
    if (!supabase || !modal || modal === 'create') return;
    setSaving(true);
    const { error: err } = await supabase
      .from('bookings')
      .update({ status: 'cancelled', updated_at: new Date().toISOString() })
      .eq('id', modal);
    setSaving(false);
    if (err) {
      setModalError(err.message);
      return;
    }
    closeModal();
    void load();
  }

  async function handleReview(action: 'confirm' | 'reject') {
    if (!supabase || !editingBooking || editingBooking.status !== 'pending') return;
    if (action === 'confirm' && form) {
      const invalid = validate(form);
      if (invalid) {
        setModalError(invalid);
        setReviewConfirm(null);
        return;
      }
      const phone = composeBookingPhone(form.phoneIso, form.phone);
      const guest_email = form.guest_email.trim()
        ? form.guest_email.trim().toLowerCase()
        : null;
      const { error: saveErr } = await supabase
        .from('bookings')
        .update({
          room_id: form.room_id,
          guest_name: form.guest_name.trim(),
          phone,
          guest_email,
          check_in: form.check_in,
          check_out: form.check_out,
          adults: form.adults,
          kids: form.kids,
          source: form.source,
          payment_option: form.payment_option,
          payment_status: form.payment_option === 'free' ? 'paid' : form.payment_status,
          amount_amd: form.payment_option === 'free' ? 0 : form.amount_amd,
          ...(form.payment_option === 'free'
            ? { payment_method: null, paid_at: new Date().toISOString() }
            : {}),
          note: form.note.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingBooking.id);
      if (saveErr) {
        setModalError(
          saveErr.code === OVERLAP_PG_CODE ? t('bookings.conflict') : saveErr.message,
        );
        setReviewConfirm(null);
        return;
      }
    }
    setReviewing(action);
    setModalError(null);
    const { data, error: err } = await supabase.functions.invoke('booking-review', {
      body: { booking_id: editingBooking.id, action },
    });
    setReviewing(null);
    if (err) {
      const msg = (data as { error?: string } | null)?.error;
      setReviewConfirm(null);
      setModalError(
        msg === 'conflict' ? t('bookings.conflict') : err.message || t('bookings.reviewError'),
      );
      return;
    }
    closeModal();
    void load();
  }

  const displayedPayStatus: BookingPaymentStatus = form
    ? form.payment_option === 'free'
      ? 'paid'
      : form.payment_option === 'on_site'
        ? ((editingBooking?.payment_status as BookingPaymentStatus) || form.payment_status)
        : form.payment_status
    : 'unpaid';

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('bookings.title')}
        subtitle={viewMode === 'calendar' ? t('bookings.subtitle') : t('bookings.subtitleTable')}
        actions={
          <div className="page-header-actions">
            <div className="segment-ctrl">
              <button
                type="button"
                className={viewMode === 'calendar' ? 'active' : ''}
                onClick={() => setViewMode('calendar')}
              >
                {t('bookings.viewCalendar')}
              </button>
              <button
                type="button"
                className={viewMode === 'table' ? 'active' : ''}
                onClick={() => setViewMode('table')}
              >
                {t('bookings.viewTable')}
              </button>
            </div>
            <button className="btn" onClick={() => openCreate()} disabled={rooms.length === 0}>
              + {t('bookings.add')}
            </button>
          </div>
        }
      />

      {error && <Callout tone="danger">{error}</Callout>}

      <div className="panel">
        <div className="bookings-cal-toolbar">
          <div className="bookings-cal-nav">
            <button className="btn-ghost" onClick={() => shiftMonth(-1)} aria-label={t('bookings.prevMonth')}>
              ‹
            </button>
            <strong>{monthLabel}</strong>
            <button className="btn-ghost" onClick={() => shiftMonth(1)} aria-label={t('bookings.nextMonth')}>
              ›
            </button>
            <button
              className="btn-ghost"
              onClick={() => {
                const now = new Date();
                setMonth({ year: now.getFullYear(), month: now.getMonth() });
              }}
            >
              {t('bookings.today')}
            </button>
          </div>
          <div className="bookings-cal-legend">
            {BOOKING_SOURCES.map((s) => (
              <span key={s} className="bookings-legend-item">
                <span className="bookings-legend-dot" style={{ background: SOURCE_COLORS[s] }} />
                {t(`bookings.source.${s}`)}
              </span>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="empty-state">{t('common.loading')}</div>
        ) : viewMode === 'table' ? (
          tableRows.length === 0 ? (
            <div className="empty-state">{t('bookings.noBookings')}</div>
          ) : (
            <div className="bookings-table-wrap">
              <table className="table bookings-table">
                <thead>
                  <tr>
                    <th>{t('bookings.guestName')}</th>
                    <th>{t('bookings.colRoom')}</th>
                    <th>{t('bookings.checkIn')}</th>
                    <th>{t('bookings.checkOut')}</th>
                    <th>{t('bookings.colNights')}</th>
                    <th>{t('bookings.sourceLabel')}</th>
                    <th>{t('bookings.amount')}</th>
                    <th>{t('bookings.paymentOption')}</th>
                    <th>{t('bookings.paymentStatusLabel')}</th>
                    <th>{t('bookings.colStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((b) => (
                    <tr
                      key={b.id}
                      className={
                        'bookings-table-row' +
                        (b.status === 'cancelled' || b.status === 'rejected'
                          ? ' is-cancelled'
                          : '') +
                        (b.status === 'pending' ? ' is-pending' : '')
                      }
                      onClick={() => openEdit(b)}
                    >
                      <td>
                        <strong>{b.guest_name}</strong>
                        <div className="bookings-table-sub">
                          {b.phone}
                          {b.guest_email ? ` · ${b.guest_email}` : ''}
                        </div>
                      </td>
                      <td>{roomName(b.room_id)}</td>
                      <td>{fmtDate(b.check_in)}</td>
                      <td>{fmtDate(b.check_out)}</td>
                      <td>{nightsBetween(b.check_in, b.check_out)}</td>
                      <td>
                        <span className="bookings-table-source">
                          <span
                            className="bookings-legend-dot"
                            style={{ background: SOURCE_COLORS[b.source as BookingSource] }}
                          />
                          {t(`bookings.source.${b.source as BookingSource}`)}
                        </span>
                      </td>
                      <td>
                        {b.payment_option === 'free'
                          ? `0 ֏`
                          : `${formatBookingAmount(Number(b.amount_amd) || 0)} ֏`}
                      </td>
                      <td>{t(`bookings.paymentOptionValue.${b.payment_option as BookingPaymentOption}`)}</td>
                      <td>
                        <span className={`bookings-table-pay status-${b.payment_status}`}>
                          {b.payment_option === 'free'
                            ? t('bookings.paymentOptionValue.free')
                            : t(`bookings.paymentStatus.${b.payment_status as BookingPaymentStatus}`)}
                        </span>
                      </td>
                      <td>
                        <span className={statusBadgeClass(b.status)}>{t(statusLabelKey(b.status))}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : rooms.length === 0 ? (
          <div className="empty-state">{t('bookings.noRooms')}</div>
        ) : (
          <div className="bookings-cal-scroll">
            <table className="bookings-cal">
              <thead>
                <tr>
                  <th className="bookings-cal-room-col">{t('bookings.colRoom')}</th>
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const date = new Date(month.year, month.month, day);
                    const dow = date.getDay();
                    const iso = isoDate(date);
                    return (
                      <th
                        key={day}
                        className={
                          (iso === today ? 'is-today ' : '') +
                          (dow === 0 || dow === 6 ? 'is-weekend' : '')
                        }
                      >
                        <span className="bookings-cal-dow">
                          {date.toLocaleDateString(locale, { weekday: 'short' })}
                        </span>
                        <span className="bookings-cal-day-num">{day}</span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rooms.map((room) => {
                  const rowBookings = grid.get(room.id);
                  return (
                    <tr key={room.id}>
                      <td className="bookings-cal-room-col" title={`${room.name} · ${room.capacity}`}>
                        <span className="bookings-room-name">{room.name}</span>
                        <span className="bookings-room-cap">×{room.capacity}</span>
                      </td>
                      {Array.from({ length: daysInMonth }, (_, i) => {
                        const day = i + 1;
                        const iso = isoDate(new Date(month.year, month.month, day));
                        const cellBookings = (rowBookings?.get(day) ?? []).filter(
                          (b) => b.status !== 'rejected',
                        );
                        const confirmed = cellBookings.find((b) => b.status === 'confirmed');
                        if (cellBookings.length === 0) {
                          return (
                            <td
                              key={day}
                              className={
                                'bookings-cal-cell free' + (iso === today ? ' is-today' : '')
                              }
                              onClick={() => openCreate(room.id, iso)}
                              title={t('bookings.clickToBook')}
                            />
                          );
                        }
                        return (
                          <td
                            key={day}
                            className={
                              'bookings-cal-cell' +
                              (confirmed ? ' booked' : ' free has-cancelled') +
                              (!confirmed && cellBookings.some((b) => b.status === 'pending')
                                ? ' has-pending'
                                : '') +
                              (iso === today ? ' is-today' : '')
                            }
                            style={
                              confirmed
                                ? {
                                    background:
                                      SOURCE_COLORS[confirmed.source as BookingSource],
                                  }
                                : undefined
                            }
                            title={
                              confirmed
                                ? undefined
                                : t('bookings.clickToBook')
                            }
                            onClick={() => {
                              if (confirmed) openEdit(confirmed);
                              else openCreate(room.id, iso);
                            }}
                          >
                            <div className="bookings-cell-stack">
                              {cellBookings.map((b) => (
                                <button
                                  key={b.id}
                                  type="button"
                                  className={
                                    'bookings-cell-chip' +
                                    (b.status === 'cancelled' ? ' is-cancelled' : '') +
                                    (b.status === 'pending' ? ' is-pending' : '')
                                  }
                                  style={{
                                    background: SOURCE_COLORS[b.source as BookingSource],
                                  }}
                                  title={`${b.guest_name} · ${b.check_in} → ${b.check_out} · ${t(`bookings.source.${b.source}`)} · ${Number(b.amount_amd).toLocaleString()} ֏ · ${t(statusLabelKey(b.status))}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEdit(b);
                                  }}
                                >
                                  {(b.guest_name.trim().charAt(0) || '·').toLocaleUpperCase()}
                                </button>
                              ))}
                            </div>
                            {!confirmed && (
                              <span className="bookings-cell-add-hint" aria-hidden>
                                +
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && form && (() => {
        const room = rooms.find((r) => r.id === form.room_id);
        const nights = nightsBetween(form.check_in, form.check_out);
        const fiscal = editingBooking ? parseBookingFiscal(editingBooking.fiscal_receipt) : null;
        const refundFiscal = editingBooking ? parseBookingFiscal(editingBooking.refund_fiscal) : null;
        const payMethod = editingBooking?.payment_method;
        const busy = saving || reviewing !== null;
        const fmtDay = (iso: string) =>
          new Date(`${iso}T00:00:00`).toLocaleDateString(i18n.language === 'hy' ? 'hy-AM' : 'en-GB', {
            day: 'numeric',
            month: 'short',
          });
        const patch = (p: Partial<BookingForm>) => setForm({ ...form, ...p });
        const quotedAmount = (rid: string, ci: string, co: string) =>
          form.payment_option === 'free'
            ? 0
            : amountForStay(rooms.find((r) => r.id === rid) ?? room, ci, co);
        const setRoom = (rid: string) =>
          patch({
            room_id: rid,
            amount_amd: quotedAmount(rid, form.check_in, form.check_out),
          });
        const setDates = (ci: string, co: string) =>
          patch({
            check_in: ci,
            check_out: co,
            amount_amd: quotedAmount(form.room_id, ci, co),
          });

        const footerLeft = (() => {
          if (editingBooking?.status === 'pending') {
            return (
              <div className="ui-row-actions">
                <Button variant="destructive" onClick={() => setReviewConfirm('reject')} disabled={busy}>
                  {t('bookings.rejectRequest')}
                </Button>
                <Button variant="secondary" onClick={() => setReviewConfirm('confirm')} disabled={busy}>
                  {t('bookings.confirmRequest')}
                </Button>
              </div>
            );
          }
          if (editingBooking?.status === 'confirmed') {
            if (bookingRequiresFiscalRefund(editingBooking)) {
              return <span className="bk-footer-note">{t('bookings.refundOnPosOnly')}</span>;
            }
            return cancelConfirm ? (
              <div className="ui-row-actions">
                <Button variant="destructive-fill" onClick={() => void handleCancelBooking()} disabled={saving}>
                  {t('bookings.cancelYes')}
                </Button>
                <Button variant="ghost" onClick={() => setCancelConfirm(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            ) : (
              <Button variant="destructive" onClick={() => setCancelConfirm(true)}>
                {t('bookings.cancelBooking')}
              </Button>
            );
          }
          return null;
        })();

        return (
          <Modal
            open
            wide
            onClose={() => !reviewConfirm && closeModal()}
            title={
              <span className="bk-title">
                {modal === 'create' ? t('bookings.add') : t('bookings.edit')}
                {editingBooking && (
                  <span className={statusBadgeClass(editingBooking.status)}>
                    {t(statusLabelKey(editingBooking.status))}
                  </span>
                )}
              </span>
            }
            caption={
              modal === 'create'
                ? undefined
                : `${editingBooking?.guest_name ?? ''} · ${fmtDay(form.check_in)} → ${fmtDay(form.check_out)}`
            }
            footerLeft={footerLeft}
            footer={
              <>
                <Button variant="ghost" onClick={closeModal} disabled={busy}>
                  {t('bookings.close')}
                </Button>
                <Button variant="primary" onClick={() => void handleSave()} disabled={busy}>
                  {saving ? '…' : t('common.save')}
                </Button>
              </>
            }
          >
            <div className="bk-stack">
              {editingBooking?.status === 'cancelled' && <Callout tone="danger">{t('bookings.cancelled')}</Callout>}
              {editingBooking?.status === 'rejected' && (
                <Callout tone="danger">{t('bookings.statusRejected')}</Callout>
              )}
              {editingBooking?.status === 'pending' && <Callout tone="info">{t('bookings.requestBanner')}</Callout>}
              {modalError && <Callout tone="danger">{modalError}</Callout>}

              <div className="bk-layout">
                <div className="bk-main">
                  {/* ── Stay ── */}
                  <section className="bk-section">
                    <h3 className="bk-section-title">Stay</h3>
                    <div className="bk-grid">
                      <Field label={t('bookings.colRoom')} htmlFor="bk-room" className="bk-span-2">
                        <select id="bk-room" value={form.room_id} onChange={(e) => setRoom(e.target.value)}>
                          {rooms.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name} · up to {r.capacity}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label={t('bookings.checkIn')} htmlFor="bk-ci">
                        <input
                          id="bk-ci"
                          type="date"
                          value={form.check_in}
                          onChange={(e) => {
                            const ci = e.target.value;
                            setDates(ci, form.check_out <= ci ? addDays(ci, 1) : form.check_out);
                          }}
                        />
                      </Field>
                      <Field
                        label={t('bookings.checkOut')}
                        htmlFor="bk-co"
                        hint={t('bookings.nights', { count: nights })}
                      >
                        <input
                          id="bk-co"
                          type="date"
                          min={addDays(form.check_in, 1)}
                          value={form.check_out}
                          onChange={(e) => setDates(form.check_in, e.target.value)}
                        />
                      </Field>
                      <Field label={t('bookings.adults')} htmlFor="bk-adults">
                        <input
                          id="bk-adults"
                          type="number"
                          min={1}
                          value={form.adults}
                          onChange={(e) => patch({ adults: Number(e.target.value) || 1 })}
                        />
                      </Field>
                      <Field label={t('bookings.kids')} htmlFor="bk-kids" optional>
                        <input
                          id="bk-kids"
                          type="number"
                          min={0}
                          value={form.kids}
                          onChange={(e) => patch({ kids: Number(e.target.value) || 0 })}
                        />
                      </Field>
                    </div>
                  </section>

                  {/* ── Guest ── */}
                  <section className="bk-section">
                    <h3 className="bk-section-title">Guest</h3>
                    <div className="bk-grid">
                      <Field label={t('bookings.guestName')} htmlFor="bk-name">
                        <input
                          id="bk-name"
                          value={form.guest_name}
                          onChange={(e) => patch({ guest_name: e.target.value })}
                          autoFocus={modal === 'create'}
                        />
                      </Field>
                      <Field label={t('bookings.phone')}>
                        <PhoneCountryInput
                          iso={form.phoneIso}
                          national={form.phone}
                          locale={locale}
                          onChange={({ iso, national }) => patch({ phoneIso: iso, phone: national })}
                        />
                      </Field>
                      <Field label={t('bookings.guestEmail')} htmlFor="bk-email" optional>
                        <input
                          id="bk-email"
                          type="email"
                          autoComplete="email"
                          value={form.guest_email}
                          onChange={(e) => patch({ guest_email: e.target.value })}
                        />
                      </Field>
                      <Field label={t('bookings.sourceLabel')} htmlFor="bk-source">
                        <select
                          id="bk-source"
                          value={form.source}
                          onChange={(e) => patch({ source: e.target.value as BookingSource })}
                        >
                          {BOOKING_SOURCES.map((s) => (
                            <option key={s} value={s}>
                              {t(`bookings.source.${s}`)}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>
                  </section>

                  {/* ── Note ── */}
                  <section className="bk-section">
                    <Field label={t('bookings.note')} htmlFor="bk-note" optional>
                      <textarea
                        id="bk-note"
                        rows={3}
                        placeholder={t('bookings.notePlaceholder')}
                        value={form.note}
                        onChange={(e) => patch({ note: e.target.value })}
                      />
                    </Field>
                  </section>
                </div>

                {/* ── Payment side panel ── */}
                <aside className="bk-side">
                  <div className={`bk-summary is-${displayedPayStatus}`}>
                    <div className="bk-summary-head">
                      <span className="bk-summary-room">{room?.name ?? '—'}</span>
                      <span className="bk-summary-dates">
                        {fmtDay(form.check_in)} → {fmtDay(form.check_out)} · {t('bookings.nights', { count: nights })}
                      </span>
                    </div>

                    <Field
                      label={t('bookings.amount')}
                      htmlFor="bk-amount"
                      hint={
                        form.payment_option === 'free'
                          ? t('bookings.freeHint')
                          : t('bookings.amountHint')
                      }
                    >
                      <input
                        id="bk-amount"
                        className="bk-amount-input"
                        type="text"
                        inputMode="numeric"
                        disabled={form.payment_option === 'free'}
                        value={
                          form.payment_option === 'free' ? '0' : formatBookingAmount(form.amount_amd)
                        }
                        onChange={(e) => patch({ amount_amd: parseBookingAmount(e.target.value) })}
                      />
                    </Field>

                    <Field label={t('bookings.paymentOption')}>
                      <SegmentedControl
                        size="sm"
                        value={form.payment_option}
                        onChange={(option) => {
                          if (option === 'free') {
                            patch({
                              payment_option: 'free',
                              payment_status: 'paid',
                              amount_amd: 0,
                            });
                            return;
                          }
                          const restoring = form.payment_option === 'free';
                          patch({
                            payment_option: option,
                            payment_status:
                              option === 'online' && !restoring
                                ? form.payment_status || 'unpaid'
                                : 'unpaid',
                            amount_amd: restoring
                              ? amountForStay(room, form.check_in, form.check_out)
                              : form.amount_amd,
                          });
                        }}
                        options={BOOKING_PAYMENT_OPTIONS.map((o) => ({
                          value: o,
                          label: t(`bookings.paymentOptionValue.${o}`),
                        }))}
                      />
                    </Field>

                    {form.payment_option === 'online' && (
                      <Field label={t('bookings.paymentStatusLabel')} htmlFor="bk-paystatus">
                        <select
                          id="bk-paystatus"
                          value={form.payment_status}
                          onChange={(e) => patch({ payment_status: e.target.value as BookingPaymentStatus })}
                        >
                          {BOOKING_PAYMENT_STATUSES.filter((s) => s !== 'failed').map((s) => (
                            <option key={s} value={s}>
                              {t(`bookings.paymentStatus.${s}`)}
                            </option>
                          ))}
                        </select>
                      </Field>
                    )}

                    <div className="bk-summary-status">
                      <span className={payBadgeClass(displayedPayStatus)}>
                        {form.payment_option === 'free'
                          ? t('bookings.paymentOptionValue.free')
                          : t(`bookings.paymentStatus.${displayedPayStatus}`)}
                      </span>
                      {form.payment_option !== 'free' && payMethod && (
                        <span className="badge badge-muted">{t(`bookings.tender.${payMethod}`)}</span>
                      )}
                    </div>
                  </div>

                  {editingBooking && form.payment_option !== 'free' && (
                    <div className={`bk-fiscal${fiscal ? ' is-paid' : ''}`}>
                      <div className="bk-fiscal-title">{t('bookings.fiscalTitle')}</div>
                      {!fiscal && !refundFiscal ? (
                        <div className="muted">{t('bookings.fiscalNone')}</div>
                      ) : (
                        <dl className="bk-fiscal-grid">
                          {fiscal && (
                            <>
                              <dt>{t('bookings.fiscalNumber')}</dt>
                              <dd>{fiscal.fiscal ?? '—'}</dd>
                              {fiscal.total != null && (
                                <>
                                  <dt>{t('bookings.fiscalTotal')}</dt>
                                  <dd>{Math.round(fiscal.total).toLocaleString()} ֏</dd>
                                </>
                              )}
                              {fiscal.crn && (
                                <>
                                  <dt>{t('bookings.fiscalCrn')}</dt>
                                  <dd>{fiscal.crn}</dd>
                                </>
                              )}
                              {fiscal.tin && (
                                <>
                                  <dt>{t('bookings.fiscalTin')}</dt>
                                  <dd>{fiscal.tin}</dd>
                                </>
                              )}
                              {fiscal.time != null && (
                                <>
                                  <dt>{t('bookings.fiscalTime')}</dt>
                                  <dd>
                                    {new Date(fiscal.time * 1000).toLocaleString(
                                      i18n.language === 'hy' ? 'hy-AM' : 'en-US',
                                    )}
                                  </dd>
                                </>
                              )}
                            </>
                          )}
                          {refundFiscal && (
                            <>
                              <dt className="bk-fiscal-sub">{t('bookings.refundFiscalTitle')}</dt>
                              <dd />
                              <dt>{t('bookings.fiscalNumber')}</dt>
                              <dd>{refundFiscal.fiscal ?? '—'}</dd>
                              {refundFiscal.total != null && (
                                <>
                                  <dt>{t('bookings.fiscalTotal')}</dt>
                                  <dd>{Math.round(refundFiscal.total).toLocaleString()} ֏</dd>
                                </>
                              )}
                            </>
                          )}
                        </dl>
                      )}
                    </div>
                  )}
                </aside>
              </div>
            </div>
          </Modal>
        );
      })()}

      {reviewConfirm && (
        <Modal
          open
          onClose={() => reviewing === null && setReviewConfirm(null)}
          title={reviewConfirm === 'confirm' ? t('bookings.confirmRequestAsk') : t('bookings.rejectRequestAsk')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setReviewConfirm(null)} disabled={reviewing !== null}>
                {t('common.cancel')}
              </Button>
              <Button
                variant={reviewConfirm === 'reject' ? 'destructive-fill' : 'primary'}
                onClick={() => void handleReview(reviewConfirm)}
                disabled={saving || reviewing !== null}
              >
                {reviewing === reviewConfirm
                  ? '…'
                  : reviewConfirm === 'confirm'
                    ? t('bookings.confirmRequestYes')
                    : t('bookings.rejectRequestYes')}
              </Button>
            </>
          }
        >
          <p className="muted" style={{ margin: 0 }}>
            {editingBooking?.guest_name} · {editingBooking ? roomName(editingBooking.room_id) : ''}
          </p>
        </Modal>
      )}
    </section>
  );
}
